import React, { useEffect, useRef, useState } from 'react';
import { audienceData } from '../../data/mock';

const AudienceInsights = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animatedValues, setAnimatedValues] = useState({
    female: 0,
    male: 0,
    ageGroups: audienceData.ageGroups.map(() => 0),
    cities: audienceData.topCities.map(() => 0),
  });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Animate numbers
          const duration = 1500;
          const steps = 60;
          const interval = duration / steps;
          let step = 0;

          const timer = setInterval(() => {
            step++;
            const progress = step / steps;
            const easeOut = 1 - Math.pow(1 - progress, 3);

            setAnimatedValues({
              female: Math.round(audienceData.gender[0].value * easeOut),
              male: Math.round(audienceData.gender[1].value * easeOut),
              ageGroups: audienceData.ageGroups.map((g) => Math.round(g.percentage * easeOut)),
              cities: audienceData.topCities.map((c) => Math.round(c.percentage * easeOut)),
            });

            if (step >= steps) clearInterval(timer);
          }, interval);

          return () => clearInterval(timer);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-[#121212]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-[#D4AF37] text-xs tracking-[0.4em] mb-4">AUDIENCE INSIGHTS</p>
          <h2 className="font-playfair text-3xl md:text-4xl text-white">
            Know Your <span className="italic text-[#D4AF37]">Audience</span>
          </h2>
        </div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Gender Split */}
          <div
            className={`bg-[#0A0A0A]/50 border border-[#D4AF37]/10 p-8 transition-all duration-1000 ease-out hover:border-[#D4AF37]/30 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <h3 className="text-white text-sm tracking-[0.2em] mb-8">GENDER SPLIT</h3>
            
            {/* Donut Chart */}
            <div className="relative w-40 h-40 mx-auto mb-8">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#3A3A3A"
                  strokeWidth="8"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#D4AF37"
                  strokeWidth="8"
                  strokeDasharray={`${animatedValues.female * 2.51} 251`}
                  className="transition-all duration-1000 ease-out"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[#D4AF37] text-2xl font-light">{animatedValues.female}%</span>
              </div>
            </div>

            <div className="flex justify-center gap-8">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#D4AF37]" />
                <span className="text-[#A1A1AA] text-sm">Female</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#3A3A3A]" />
                <span className="text-[#A1A1AA] text-sm">Male</span>
              </div>
            </div>
          </div>

          {/* Age Distribution */}
          <div
            className={`bg-[#0A0A0A]/50 border border-[#D4AF37]/10 p-8 transition-all duration-1000 ease-out hover:border-[#D4AF37]/30 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '400ms' }}
          >
            <h3 className="text-white text-sm tracking-[0.2em] mb-8">AGE DISTRIBUTION</h3>
            
            <div className="space-y-6">
              {audienceData.ageGroups.map((group, index) => (
                <div key={group.range}>
                  <div className="flex justify-between mb-2">
                    <span className="text-[#A1A1AA] text-sm">{group.range}</span>
                    <span className="text-white text-sm">{animatedValues.ageGroups[index]}%</span>
                  </div>
                  <div className="h-2 bg-[#1A1A1A] overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] transition-all duration-1000 ease-out"
                      style={{ width: `${animatedValues.ageGroups[index]}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Cities */}
          <div
            className={`bg-[#0A0A0A]/50 border border-[#D4AF37]/10 p-8 transition-all duration-1000 ease-out hover:border-[#D4AF37]/30 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '600ms' }}
          >
            <h3 className="text-white text-sm tracking-[0.2em] mb-8">TOP CITIES</h3>
            
            <div className="space-y-5">
              {audienceData.topCities.map((city, index) => (
                <div key={city.city} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-[#D4AF37] text-xs">{String(index + 1).padStart(2, '0')}</span>
                    <span className="text-white">{city.city}</span>
                  </div>
                  <span className="text-[#A1A1AA]">{animatedValues.cities[index]}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AudienceInsights;
